import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import { Slot } from '@radix-ui/react-slot';
import type { Ref } from 'react';
import type { TagProps } from 'types';
import { iconVariants, tagVariants, textAreaVariants } from 'variants/variants';

export type { TagVariant } from 'types';

const Tag = ({
  className,
  variant = 'primary',
  size = 'small',
  fill,
  asChild = false,
  children,
  selected = false,
  disabled = false,
  onRemove,
  onClick,
  spanProps = {},
  rawProps = {},
  'data-testid': testID,
  readOnly = false,
  ref,
}: TagProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const Comp = asChild ? Slot : 'div';
  const isClickable = !readOnly && !!onClick;
  return (
    <Comp
      aria-selected={selected}
      aria-disabled={disabled}
      data-clickable={isClickable}
      ref={ref}
      data-testid={testID}
      {...rawProps}
      className={cn(
        tagVariants({
          variant,
          fill,
          size,
          clickable: isClickable,
        }),
        className,
        rawProps.className,
      )}
      onClick={readOnly ? undefined : onClick}
    >
      <div className={'relative flex w-full items-center'}>
        <span
          {...spanProps}
          className={cn(textAreaVariants({ size }), spanProps.className)}
        >
          {children}
        </span>
        {onRemove && !readOnly && (
          <Icon
            name="x-close"
            className={cn(iconVariants({ size }))}
            onClick={onRemove}
            data-testid="remove-icon"
          />
        )}
      </div>
    </Comp>
  );
};

Tag.displayName = 'Tag';
export { Tag };
